/**
 * The practice range's control strip.
 *
 * Every dial here is a *request*: the room clamps each value and echoes it back
 * through synced state, and this reads what came back rather than what was
 * asked for. Nothing on this panel can change how a cast is judged — only how
 * often and how fast the dummy shoots, which is the one thing worth rehearsing
 * against at several settings.
 */
import { PRACTICE, PROJECTILE_SPEED_SCALE } from '@spell/shared-types';
import { C2S, type PracticeControlMsg } from '@spell/network-protocol';
import { net } from '../networking/net';
import { useNet } from '../networking/netStore';
import { sfx } from '../audio/sfx';

export interface PracticePanelProps {
  /** True while the pointer is locked, i.e. the player is actually duelling. */
  playing: boolean;
  onLeave: () => void;
}

export function PracticePanel({ playing, onLeave }: PracticePanelProps) {
  const practice = useNet((s) => s.practice);
  const speedScale = useNet((s) => s.speedScale);

  const send = (patch: PracticeControlMsg) => {
    net.send(C2S.PRACTICE_CONTROL, patch);
    sfx.ui();
  };

  return (
    <div className={`practice-panel ${playing ? 'practice-panel--dim' : ''}`} data-testid="practice-panel">
      <div className="practice-panel__title">
        <span>연습장</span>
        {/* The one thing a player has to know here, and the one thing they
            cannot discover: while the pointer is locked there is no cursor to
            reach these dials with. Said on the panel itself rather than in a
            hint elsewhere, because this is where they will be looking when
            they want it. */}
        <span className="practice-panel__esc">
          {playing ? 'ESC 로 조작' : '조작 가능'}
        </span>
      </div>

      <label className="practice-row">
        <input
          type="checkbox"
          data-testid="practice-fire"
          checked={practice.fire}
          onChange={(e) => send({ dummyFire: e.target.checked })}
        />
        <span>훈련 인형이 공격함</span>
      </label>

      <label className="practice-row">
        <span className="practice-row__label">공격 간격</span>
        <input
          type="range"
          data-testid="practice-interval"
          min={PRACTICE.FIRE_INTERVAL_MS.min}
          max={PRACTICE.FIRE_INTERVAL_MS.max}
          step={100}
          value={practice.intervalMs}
          onChange={(e) => send({ intervalMs: Number(e.target.value) })}
        />
        <span className="practice-row__value">{(practice.intervalMs / 1000).toFixed(1)}s</span>
      </label>

      <label className="practice-row">
        <span className="practice-row__label">탄속 배율</span>
        <input
          type="range"
          data-testid="practice-speed"
          min={PROJECTILE_SPEED_SCALE.min}
          max={PROJECTILE_SPEED_SCALE.max}
          step={0.05}
          value={speedScale}
          onChange={(e) => send({ speedScale: Number(e.target.value) })}
        />
        <span className="practice-row__value">{speedScale.toFixed(2)}×</span>
      </label>

      <div className="hint" style={{ marginTop: 2 }}>
        배율은 <b>내 주문까지</b> 포함해 이 방의 모든 투사체에 적용됩니다 — 대련장과 같은 규칙이라, 여기서 익힌 감각이
        그대로 통합니다.
        <br />
        패링 판정 구간은 어느 배율에서도 동일한 시간입니다. 느린 방은 읽을 시간이 길 뿐입니다.
      </div>

      <div className="row" style={{ marginTop: 10 }}>
        <button type="button" className="btn" data-testid="practice-restore" onClick={() => send({ restore: true })}>
          회복 · 초기화
        </button>
        <button type="button" className="btn btn--ghost" data-testid="practice-leave" onClick={onLeave}>
          로비로
        </button>
      </div>
    </div>
  );
}
